import { getCsrfToken } from './csrf';

/**
 * Persists the active-role selection to the SERVER session.
 *
 * ## Why this exists
 *
 * The active role used to travel only as the `X-Matrix-Active-Role` header on
 * Apollo requests. Headers reach GraphQL and nothing else: LiveView pages
 * (Dashboards, the whole Admin Console) authenticate over a WebSocket and carry
 * no headers, so they built a backend ctx with the user's FULL role set and
 * ignored the switcher entirely — a restricted role still saw every dashboard.
 *
 * `POST /auth/active-role` stores the selection in the session, which BOTH
 * transports read (`MatrixAuth.ActiveRole`). The header is still sent as well,
 * so a single GraphQL request can override the stored value.
 *
 * De-escalation only: the backend refuses a role the user doesn't hold, and
 * re-verifies on every request.
 *
 * Failures are swallowed deliberately. The header path keeps GraphQL correct on
 * its own, so a transient write failure degrades to "LiveView still shows the
 * previous role" rather than blocking the switch or throwing in a click
 * handler. The rejection is logged for diagnosis.
 */
export async function persistActiveRole(role: string | null): Promise<void> {
  if (typeof fetch === 'undefined') return;

  const csrfToken = getCsrfToken();
  const body = new URLSearchParams();
  body.set('_csrf_token', csrfToken);
  body.set('role', role ?? '');

  try {
    const res = await fetch('/auth/active-role', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        `persistActiveRole: server rejected role change (${res.status}); ` +
          'LiveView surfaces may still reflect the previous role until the next switch'
      );
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('persistActiveRole: request failed', err);
  }
}
